/**
 * Prepayment progress toward the first credit limit (advance-payment customers only).
 * Wraps GET account/prepayment-progress. Server-side computed; the store only caches it,
 * de-duplicates concurrent loads and exposes refresh() for after a confirmed payment.
 */
export const usePrepaymentProgressStore = defineStore("prepaymentProgress", () => {
  const FRESH_MS = 60_000;

  const progress = ref(null);
  const loading = ref(false);
  const error = ref(null);
  const loadedAt = ref(0);
  let inflight = null;

  const isApplicable = computed(() => !!progress.value?.isApplicable);

  const load = async (force = false) => {
    if (inflight) return inflight;
    if (!force && progress.value && Date.now() - loadedAt.value < FRESH_MS) {
      return progress.value;
    }

    loading.value = true;
    error.value = null;
    inflight = (async () => {
      const { fetchWithToken } = useFetchApiWithToken();
      const url = useCldURLs[CLD_ENV].account.prepayment_progress;
      const data = await fetchWithToken(url, { method: "GET", requireAuth: true });
      progress.value = data ?? null;
      loadedAt.value = Date.now();
      return progress.value;
    })()
      .catch((err) => {
        error.value = err;
        console.error("getPrepaymentProgress: API ERROR:", err);
        throw err;
      })
      .finally(() => {
        inflight = null;
        loading.value = false;
      });

    return inflight;
  };

  const refresh = () => load(true);

  // A confirmed Adyen payment is recorded by the webhook, which can land a few seconds after
  // the drop-in reports success. Refresh now and once more shortly after.
  const refreshAfterPayment = (delayMs = 5000) => {
    refresh().catch(() => {});
    setTimeout(() => {
      refresh().catch(() => {});
    }, delayMs);
  };

  const reset = () => {
    progress.value = null;
    loading.value = false;
    error.value = null;
    loadedAt.value = 0;
    inflight = null;
  };

  return {
    progress,
    loading,
    error,
    isApplicable,
    load,
    refresh,
    refreshAfterPayment,
    reset,
  };
});
